import { Code, ListTodo, FolderKanban, ChartPie } from 'lucide-react';
import { useUser } from '@/shared/hooks/useAuth';
import { useITProjects } from '../widgets/it/hooks/useITProjects';
import { useITTasks } from '../widgets/it/hooks/useITTasks';
import { DashboardHero } from '../infrastructure/widgets/DashboardHero';
import { DonutChart } from '../infrastructure/widgets/DonutChart';
import { SectionCard, SimpleList, SimpleListItem } from '../infrastructure/widgets/DashboardKit';

const TASK_PRIORITY_COLOR: Record<string, string> = { critical: '#f04438', high: '#f79009', medium: '#5b45d6', low: '#667085' };
const STATUS_COLOR: Record<string, string> = { todo: '#667085', in_progress: '#5b45d6', review: '#f79009', done: '#12b76a' };

export function ITEmployeeDashboard() {
  const user = useUser();
  const { data: projects = [] } = useITProjects();
  const { data: tasks = [] } = useITTasks();

  const openTasks = tasks.filter((t) => t.status !== 'done');
  const activeProjects = projects.filter((p) => p.status !== 'done' && p.status !== 'completed');

  const statusCounts = tasks.reduce<Record<string, number>>((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {});

  const taskItems: SimpleListItem[] = [...openTasks]
    .sort((a, b) => (a.due_date && b.due_date ? new Date(a.due_date).getTime() - new Date(b.due_date).getTime() : 0))
    .slice(0, 6)
    .map((t) => ({
      icon: ListTodo,
      title: t.title,
      meta: t.project_name || 'Unassigned project',
      tag: t.priority,
      tagHex: TASK_PRIORITY_COLOR[t.priority] || '#5b45d6',
    }));

  const projectItems: SimpleListItem[] = activeProjects.slice(0, 6).map((p) => ({
    icon: FolderKanban,
    title: p.name,
    meta: `Status: ${p.status.replace('_', ' ')}`,
    tag: p.priority,
    tagHex: TASK_PRIORITY_COLOR[p.priority] || '#7a63de',
  }));

  return (
    <div className="mx-auto max-w-[1440px]">
      <DashboardHero
        badge="IT & Systems · My Desk"
        badgeIcon={Code}
        title={`Welcome back, ${user?.displayName || 'there'}`}
        subtitle="Your tasks and active projects for today."
        heroStats={[
          // The same three figures were shown twice — here and again as a card
          // row underneath. Once, with their icons, is enough.
          { label: 'Open tasks', value: String(openTasks.length), sub: `of ${tasks.length} total`, icon: ListTodo, tone: 'primary' },
          { label: 'Active projects', value: String(activeProjects.length), sub: `of ${projects.length} total`, icon: FolderKanban, tone: 'info' },
          { label: 'All my tasks', value: String(tasks.length), sub: 'assigned to me', icon: ChartPie, tone: 'neutral' },
        ]}
      />

      <div className="grid grid-cols-12 gap-4">
        {Object.keys(statusCounts).length > 0 && (
          <SectionCard icon={ChartPie} title="Task Status" subtitle="All my tasks" span={4}>
            <DonutChart
              segments={Object.entries(statusCounts).map(([status, count]) => ({
                label: status.replace('_', ' '),
                value: count,
                color: STATUS_COLOR[status] || '#667085',
              }))}
              centerValue={String(tasks.length)}
              centerLabel="Tasks"
            />
          </SectionCard>
        )}
        <SectionCard icon={ListTodo} title="My Tasks" subtitle={`${openTasks.length} open`} span={8}>
          <SimpleList items={taskItems} />
        </SectionCard>
        <SectionCard icon={FolderKanban} title="My Projects" subtitle={`${activeProjects.length} active`} span={12}>
          <SimpleList items={projectItems} />
        </SectionCard>
      </div>
    </div>
  );
}
